import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import {
    GLOBAL_CLICK_HANDLED_LAYER_IDS,
    GLOBAL_CLICK_LAYER_CATEGORIES,
    type GlobalClickCategory,
} from '@/components/map/utils/mapLayerOrder';

/** What one physical map click turned out to be, decided from a single hit test. */
export type MapClickOutcome = GlobalClickCategory | 'empty';

/**
 * Fixed decision order when a click hits layers from more than one category. Explicit
 * and code-defined: the classifier must never depend on the order MapLibre returns
 * rendered features, which is unspecified as far as this module is concerned. The
 * priorities mirror what the user visibly aimed at — individual markers render above
 * cluster bubbles, which render above the polygon fills — but the render stack is only
 * the motivation, never the mechanism.
 */
const CATEGORY_PRIORITY: readonly GlobalClickCategory[] = [
    'poi',
    'valve',
    'poiCluster',
    'valveCluster',
    // vans draw in assetOverlays, below the POI pins and cluster surfaces but above the
    // polygon fills — mirroring the hover precedence
    'van',
    'polygon',
];

/**
 * Classifies a click from the set of handled-layer ids that reported a rendered feature
 * under it. Deterministic for any ordering of `hitLayerIds`.
 */
export function classifyMapClick(hitLayerIds: Iterable<string>): MapClickOutcome {
    const hits = new Set(hitLayerIds);
    for (const category of CATEGORY_PRIORITY) {
        if (GLOBAL_CLICK_LAYER_CATEGORIES[category].some((id) => hits.has(id))) return category;
    }
    return 'empty';
}

/**
 * Cross-feature clearing outcomes, one callback per classified click. The hook never
 * selects, pins, zooms or opens anything — those stay with the delegated feature
 * handlers — so a `poi` outcome has no callback at all: a POI click needs nothing
 * cleared, only the guarantee that it is never treated as empty.
 */
export interface MapClickDismissalCallbacks {
    /** No handled layer under the click: the one place an empty-map click is decided. */
    onEmptyClick?: () => void;
    /** A POI bubble/count surface (network level or an explorer cell) was the click target. */
    onPoiClusterClick?: () => void;
    /** An air-valve cluster/count surface was the click target. */
    onValveClusterClick?: () => void;
    /** An individual air valve was the click target. */
    onValveClick?: () => void;
    /** An AOI/DMA fill was the topmost handled surface under the click. */
    onPolygonClick?: () => void;
}

/**
 * The single authoritative global click listener for the Orbit map. Every feature hook
 * used to carry its own plain map `click` handler to decide "was that click empty?",
 * each with a partial view of the layers — so a POI click with no rendered polygon
 * underneath was selected by one handler and immediately cleared as "empty" by another.
 * This hook replaces all of them: one plain listener, one rendered-feature query over
 * the named handled-layer list, one deterministic outcome per physical click.
 *
 * Not a router: feature selection, valve pinning and cluster zooming remain delegated
 * to their owning hooks. This listener only coordinates clearing and dismissal, wired
 * by MapView to the interaction commands and the air-valve card's clear function.
 *
 * Plain listeners are deduped by identity and survive style reloads, so this registers
 * once per map instance and never re-registers on `style.load`. Callbacks are read
 * through a ref so rerenders with new callback identities never touch the listener.
 */
export function useMapClickDismissal(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    callbacks: MapClickDismissalCallbacks,
): void {
    const callbacksRef = useRef(callbacks);
    useEffect(() => {
        callbacksRef.current = callbacks;
    });

    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const handleMapClick = (e: maplibregl.MapMouseEvent) => {
            // Only query layers that currently exist — protects the click path during
            // initial load, style changes and mode-specific layer sets, and
            // queryRenderedFeatures throws on unknown ids.
            const layers = GLOBAL_CLICK_HANDLED_LAYER_IDS.filter((id) => mlMap.getLayer(id));
            // With no handled layer present the map's state is indeterminate (mid style
            // swap, pre-first-load) — never make a clearing decision from zero information.
            if (!layers.length) return;

            const features = mlMap.queryRenderedFeatures(e.point, { layers });
            const outcome = classifyMapClick(features.map((f) => f.layer.id));

            switch (outcome) {
                case 'poi':
                    // The delegated POI handler owns the selection; nothing to clear.
                    return;
                case 'valve':
                    callbacksRef.current.onValveClick?.();
                    return;
                case 'poiCluster':
                    callbacksRef.current.onPoiClusterClick?.();
                    return;
                case 'valveCluster':
                    callbacksRef.current.onValveClusterClick?.();
                    return;
                case 'van':
                    // The delegated van handler owns the selection (SELECT_FEATURE replaces
                    // the whole slice); vans and the SAVI valve card are mode-exclusive, so
                    // there is nothing else to clear.
                    return;
                case 'polygon':
                    callbacksRef.current.onPolygonClick?.();
                    return;
                case 'empty':
                    callbacksRef.current.onEmptyClick?.();
            }
        };

        mlMap.on('click', handleMapClick);
        return () => {
            mlMap.off('click', handleMapClick);
        };
    }, [isMapLoaded, mapRef]);
}
